/* Allure Contact — Phase 2 editorial revamp.
   Hairline-underline form (no boxes), editorial studio panel on right.
   Preserves the existing POST /contact handler with same field shape
   { name, email, phone, message }. */
import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  ChapterEyebrow, DisplayHeadline, GoldRule, GoldFoilButton, FilmGrain,
} from "@/components/editorial";
import useEditorialReveal from "@/hooks/useEditorialReveal";
import useStructuredData from "@/hooks/useStructuredData";
import { allureLocalBusinessSchema, allureContactPageSchema } from "@/lib/schemas";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  useEditorialReveal();
  useStructuredData([allureLocalBusinessSchema(), allureContactPageSchema()]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a short message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Letter sent. We will reply within two business days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="relative bg-[color:var(--cream)] text-[color:var(--cream-ink)] min-h-screen">
      <FilmGrain opacity={0.04} tone="warm" />
      <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <GoldRule width="2rem" />
        <div className="mt-3"><ChapterEyebrow chapter="IV" label="Correspondence" /></div>
        <DisplayHeadline size="hero" className="mt-8 max-w-3xl" data-reveal="rise">
          Write to us, <em>kindly</em>.
        </DisplayHeadline>
        <p className="font-serif italic text-xl md:text-2xl mt-6 text-[color:var(--cream-ink)]/70" data-reveal="rise">
          We answer every letter within two business days.
        </p>

        <div className="mt-16 lg:mt-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20">
          {/* LEFT — hairline-underline form */}
          <form onSubmit={onSubmit} className="space-y-9" data-testid="contact-form">
            {[
              { name: "name",    label: "Your name", type: "text",  placeholder: "Jane Doe" },
              { name: "email",   label: "Email",     type: "email", placeholder: "jane@studio.com" },
              { name: "phone",   label: "Telephone (optional)", type: "tel", placeholder: "+1 555 0100" },
            ].map((f) => (
              <label key={f.name} className="block">
                <span className="ed-eyebrow text-[color:var(--gold-deep)] mb-3 block">{f.label}</span>
                <input
                  type={f.type}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  placeholder={f.placeholder}
                  className="w-full bg-transparent border-0 border-b border-[color:var(--gold-base)]/45 py-3 outline-none focus:border-[color:var(--gold-base)] placeholder:text-[color:var(--cream-ink)]/30 text-[color:var(--cream-ink)] text-lg transition-colors"
                  data-testid={`contact-${f.name}`}
                />
              </label>
            ))}
            <label className="block">
              <span className="ed-eyebrow text-[color:var(--gold-deep)] mb-3 block">Message</span>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                placeholder="Tell us about the moment you'd like remembered."
                className="w-full bg-transparent border-0 border-b border-[color:var(--gold-base)]/45 py-3 outline-none focus:border-[color:var(--gold-base)] placeholder:text-[color:var(--cream-ink)]/30 text-[color:var(--cream-ink)] text-lg transition-colors resize-none"
                data-testid="contact-message"
              />
            </label>
            <div className="pt-2">
              <GoldFoilButton onClick={undefined} type="submit" disabled={sending}>
                {sending ? "Posting…" : "Post your letter"}
              </GoldFoilButton>
            </div>
            {sent ? (
              <p className="font-serif italic text-[color:var(--gold-deep)] mt-2" data-testid="contact-success">
                — Letter received. We'll reply within two business days.
              </p>
            ) : null}
          </form>

          {/* RIGHT — editorial studio panel */}
          <aside data-reveal="rise">
            <div className="p-7 lg:p-9 border border-[color:var(--cream-ink)]/12 bg-[color:var(--cream-paper)]">
              <div className="ed-eyebrow text-[color:var(--gold-deep)]">The Studio</div>
              <GoldRule width="2rem" className="mt-3 mb-6" />
              <p className="font-serif italic text-2xl leading-tight text-[color:var(--cream-ink)]">
                Allure Image<br />
                212 Hawthorne Lane<br />
                Newark, NJ · 07102
              </p>
              <div className="mt-7 relative aspect-[4/5] overflow-hidden bg-[color:var(--cream)]">
                <img src="/allure/studio-interior.jpg" alt="Studio interior" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="mt-7">
                <div className="ed-eyebrow text-[color:var(--gold-deep)] mb-3">Office hours</div>
                <p className="font-serif italic text-lg">Tue — Sat / 10:00 — 18:00</p>
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-16 flex items-center gap-4">
          <GoldRule width="100%" tone="soft" />
          <span className="ed-eyebrow text-[color:var(--gold-deep)] whitespace-nowrap">By appointment</span>
          <GoldRule width="100%" tone="soft" />
        </div>
      </div>
    </div>
  );
}
